import React, { FC, useCallback, useState } from "react"
import * as Yup from "yup"
import { Button, CircularProgress, Modal, TextField, Checkbox, FormControlLabel } from "@material-ui/core"
import { Formik, Field } from "formik"
import { createCommunity } from "../../../../actions"
import styles from "./styles.module.scss"
import { URL_REGEX } from "../../../../helpers"
import { CommunityFormValues } from "./types"

interface AddCommunityModalProps {
  hideModal: () => void
}

export const AddCommunityModal: FC<AddCommunityModalProps> = ({ hideModal }) => {
  const [errorModal, setErrorModal] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>("")
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const handleClickCreate = useCallback(async (values: CommunityFormValues) => {
    setIsLoading(true)

    const { name, description, img_url, owner_user_id, game_ids, is_private } = values

    const response = await createCommunity(
      name,
      description,
      img_url,
      owner_user_id,
      game_ids ? game_ids.split(",").map(s => s.trim()) : null,
      is_private,
    )

    if (response?.error) {
      setErrorMessage(response?.message || response?.error)
      handleOpenErrorModal()
    } else {
      hideModal()
    }

    setIsLoading(false)
  }, [])

  const handleOpenErrorModal = () => setErrorModal(true)
  const handleCloseErrorModal = () => setErrorModal(false)

  return (
    <div className={styles.modalBody}>
      <h2>Add Community</h2>
      <Modal className={styles.modal} open={errorModal} onClose={handleCloseErrorModal}>
        <div className={styles.errorModal}>
          <h2>{errorMessage}</h2>
        </div>
      </Modal>
      <Formik
        initialValues={{
          name: "",
          description: "",
          owner_user_id: "",
          img_url: "",
          game_ids: "",
          is_private: false,
        }}
        onSubmit={handleClickCreate}
        validationSchema={Yup.object().shape({
          name: Yup.string().required().max(50),
          description: Yup.string().required().max(500),
          img_url: Yup.string().required().matches(URL_REGEX),
          owner_user_id: Yup.string().required(),
          game_ids: Yup.string(),
          is_private: Yup.boolean(),
        })}>
        {({ handleChange, handleSubmit, errors, values }) => (
          <>
            <TextField
              className={styles.modalInput}
              fullWidth
              required
              label="Community Name"
              onChange={handleChange("name")}
              placeholder="Community Name"
              error={!!errors.name}
              value={values.name}
              disabled={isLoading}
            />
            <TextField
              className={styles.modalInput}
              fullWidth
              required
              label="Description"
              onChange={handleChange("description")}
              placeholder="Description"
              error={!!errors.description}
              value={values.description}
              disabled={isLoading}
            />
            <TextField
              className={styles.modalInput}
              fullWidth
              label="Community Image URL"
              onChange={handleChange("img_url")}
              placeholder="Community Image URL"
              error={!!errors.img_url}
              value={values.img_url}
              disabled={isLoading}
            />
            <TextField
              className={styles.modalInput}
              fullWidth
              label="Owner User ID"
              onChange={handleChange("owner_user_id")}
              placeholder="Owner User ID"
              error={!!errors.owner_user_id}
              value={values.owner_user_id}
              disabled={isLoading}
            />
            <TextField
              className={styles.modalInput}
              fullWidth
              required
              label="Game IDs"
              onChange={handleChange("game_ids")}
              placeholder="Game IDs"
              error={!!errors.game_ids}
              value={values.game_ids}
              disabled={isLoading}
            />
            <FormControlLabel
              control={<Checkbox name="is_private" checked={!!values.is_private} onChange={handleChange} />}
              label="Private"
              style={{ width: "100%" }}
            />
            <div className={styles.buttonContainer}>
              {isLoading ? (
                <CircularProgress size={30} />
              ) : (
                <Button variant="contained" size="small" onClick={() => handleSubmit()}>
                  Create
                </Button>
              )}
            </div>
          </>
        )}
      </Formik>
    </div>
  )
}
